import { Football, ArrowRight, ArrowLeft, Lightning, Check, Question } from "@phosphor-icons/react";
import { Link } from "react-router-dom";
import SEO from "@/components/SEO";
import { useIsAppNavVisible } from "@/hooks/useIsAppNavVisible";
import PublicNav from "@/components/PublicNav";

const FAQ_ITEMS = [
  { q: "Do I need to make an account?", a: "No — importing a league to see roster values, rankings, and the free Trade Calculator requires nothing. An account is only needed if you want to save settings or go Pro." },
  { q: "Is my Sleeper data ever written back or changed?", a: "No — the connection is read-only." },
  { q: "What if I'm in more than one dynasty league?", a: "All of them load under your one Sleeper username — switch between leagues from your dashboard." },
];

const FEATURES = [
  { title: "Your whole roster gets real values instantly", desc: "Every player on every team in your league is priced against the live dynasty chart." },
  { title: "The AI Trade Analyzer knows your actual team", desc: "Pick a strategy — Win Now, Balanced, or Rebuild — and it generates six full trade packages built from real rosters in your league." },
  { title: "Trade Finder scans your whole league in one click", desc: "Surfacing trades where both sides actually improve." },
  { title: "Contender Rankings tell you who's buying and who's selling", desc: "Every team scored 0-100." },
  { title: "Draft War Room pulls your real draft board", desc: "Including traded picks, with pick-by-pick AI advice and a mock draft simulator." },
];

export default function SleeperSetupGuide() {
  const hideNav = useIsAppNavVisible();
  return (
    <div className="min-h-screen bg-[#0a0a0a]">
      <SEO title="How to Connect Your Sleeper Dynasty League — Dynasty Trade Maker" description="Step-by-step guide to importing your Sleeper dynasty league into Dynasty Trade Maker for AI trade suggestions, roster values, and draft tools — free, takes under a minute." path="/guide/sleeper-setup" />

      <PublicNav />

      <div className="max-w-3xl mx-auto px-4 py-10">
        <Link to="/guide" className="inline-flex items-center gap-1 text-[10px] text-[#555] hover:text-[#a3a3a3] no-underline mb-6 uppercase tracking-wider"><ArrowLeft size={10} /> GUIDE</Link>

        <span className="text-[10px] font-bold uppercase tracking-[0.3em] text-[#ccff00] block mb-2">SETUP GUIDE</span>
        <h1 className="font-heading text-3xl sm:text-4xl lg:text-5xl tracking-tight text-white uppercase mb-4" data-testid="sleeper-setup-title">HOW TO CONNECT YOUR <span className="text-[#ccff00]">SLEEPER LEAGUE</span></h1>
        <p className="text-base text-[#a3a3a3] leading-relaxed mb-8">Dynasty Trade Maker is built specifically for Sleeper — no other platform gets the same level of integration. Here's exactly what happens when you connect your league, and what you unlock once you do.</p>

        <div className="space-y-10">
          {/* Setup Steps */}
          <section>
            <h2 className="font-heading text-2xl text-white uppercase mb-3">SETUP: UNDER A MINUTE, NO SLEEPER PASSWORD NEEDED</h2>
            <p className="text-sm text-[#a3a3a3] leading-relaxed mb-4">Sleeper's API is public and read-only for league data, which means you connect with just your Sleeper username — never a password, never an OAuth login screen. Click Import Your League, enter your username, and every dynasty league tied to that account loads automatically.</p>
            <div className="border border-[#ccff00]/30 bg-[#ccff00]/5 p-5 flex items-start gap-3">
              <Check size={18} className="text-[#ccff00] flex-shrink-0 mt-0.5" weight="bold" />
              <div>
                <p className="text-sm text-white font-semibold mb-1">No password. No OAuth. Just your username.</p>
                <p className="text-xs text-[#888]">Sleeper's public API means your credentials are never shared with us.</p>
              </div>
            </div>
          </section>

          {/* What Changes */}
          <section>
            <h2 className="font-heading text-2xl text-white uppercase mb-4">WHAT CHANGES ONCE YOU'RE CONNECTED</h2>
            <div className="space-y-3">
              {FEATURES.map((f, i) => (
                <div key={i} className="flex items-start gap-3 border border-[#262626] bg-[#171717] p-4">
                  <div className="w-6 h-6 bg-[#ccff00]/10 flex items-center justify-center rounded-sm flex-shrink-0 mt-0.5">
                    <span className="text-[10px] font-bold text-[#ccff00]">{i + 1}</span>
                  </div>
                  <div>
                    <p className="text-sm text-white font-semibold mb-0.5">{f.title}</p>
                    <p className="text-xs text-[#888]">{f.desc}</p>
                  </div>
                </div>
              ))}
            </div>
          </section>

          {/* Why Sleeper */}
          <section>
            <h2 className="font-heading text-2xl text-white uppercase mb-3">WHY THIS MATTERS FOR DYNASTY SLEEPER LEAGUES</h2>
            <p className="text-sm text-[#a3a3a3] leading-relaxed">Sleeper is the default platform for most competitive dynasty leagues, and dynasty formats (especially Superflex, TE Premium) shift value in ways a generic redraft tool won't catch. Because we pull your actual league settings on import, every value and suggestion you see already accounts for your specific format.</p>
          </section>

          {/* FAQ */}
          <section>
            <h2 className="font-heading text-2xl text-white uppercase mb-4">FAQ</h2>
            <div className="space-y-3">
              {FAQ_ITEMS.map((item, i) => (
                <div key={i} className="border border-[#262626] bg-[#171717] p-4">
                  <div className="flex items-start gap-2">
                    <Question size={14} className="text-[#ccff00] flex-shrink-0 mt-0.5" weight="bold" />
                    <div>
                      <p className="text-sm text-white font-semibold mb-1">{item.q}</p>
                      <p className="text-xs text-[#888]">{item.a}</p>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </section>

          {/* CTA */}
          <div className="border border-[#ccff00]/30 bg-[#ccff00]/5 p-6 text-center">
            <h3 className="font-heading text-lg text-white uppercase mb-2">READY TO CONNECT?</h3>
            <p className="text-xs text-[#888] mb-4">Import your Sleeper league in under a minute. See what's free vs. Pro.</p>
            <div className="flex flex-col sm:flex-row items-center justify-center gap-3">
              <a href="/" className="h-9 px-5 btn-primary rounded-sm text-[11px] inline-flex items-center gap-1.5 no-underline font-bold"><Lightning size={12} weight="fill" /> IMPORT YOUR LEAGUE</a>
              <Link to="/billing" className="text-[11px] text-[#ccff00] no-underline hover:underline flex items-center gap-1">See Free vs. Pro <ArrowRight size={11} /></Link>
            </div>
          </div>
        </div>

        <div className="mt-8 text-center text-[10px] text-[#444] space-x-3">
          <Link to="/guide" className="hover:text-[#a3a3a3] no-underline">Guide</Link>
          <Link to="/guide/superflex-strategy" className="hover:text-[#a3a3a3] no-underline">Superflex Strategy</Link>
          <Link to="/rankings" className="hover:text-[#a3a3a3] no-underline">Rankings</Link>
          <span>dynastytrademaker.com</span>
        </div>
      </div>
    </div>
  );
}
